import { formatLocaleCurrency, formatLocaleDate, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Alert, Descriptions, DescriptionsProps, Divider } from "antd";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { Permission } from "../../../../common/security/enums";
import { AGENT_ROUTE_PATHS } from "../../../agent/paths";
import { formatAgentName } from "../../../agent/utils";
import { useHasPermissions } from "../../../auth/hooks";
import { CommissionBatchName } from "../../components/CommissionBatchName";
import { CommissionAdvanceStatus } from "../enums";
import { CommissionAdvance } from "../types";
import { CommissionAdvanceStatusTag } from "./CommissionAdvanceStatusTag";
import { CommissionAdvanceCyclesTable } from "./tables/CommissionAdvanceCyclesTable";
import { CommissionAdvanceInputsTable } from "./tables/CommissionAdvanceInputsTable";

interface Props {
  advance: CommissionAdvance;
}

export const CommissionAdvanceDetail = ({ advance }: Props) => {
  const { t } = useTranslation();

  const hasCommissionsPermission = useHasPermissions(Permission.COMMISSIONS);
  const hasReadAgentPermission = useHasPermissions(Permission.AGENT_READ);

  const items = useMemo<DescriptionsProps["items"]>(() => {
    const items: DescriptionsProps["items"] = [
      {
        label: t("commissions.advance.enums.status._label"),
        children: <CommissionAdvanceStatusTag status={advance.status} />,
      },
      {
        label: t("commissions.advance.attrs.startBatch"),
        children: advance.startBatch ? (
          <CommissionBatchName
            batchName={advance.startBatch.name}
            batchId={advance.startBatch.id}
            showLink={hasCommissionsPermission}
          />
        ) : (
          t("commissions.advance.attrs.manuallyCreated")
        ),
        span: 2,
      },
    ];

    items.push(
      {
        label: t("commissions.advance.attrs.feeTotalAmount"),
        children: formatLocaleCurrency(advance.feeTotalAmount),
      },
      {
        label: t("commissions.advance.attrs.feePaymentsCount"),
        children: advance.feePaymentsCount,
      },
      {
        label: t("commissions.advance.attrs.feePaymentAmount"),
        children: formatLocaleCurrency(advance.feePaymentAmount),
      },
    );

    if (hasCommissionsPermission) {
      items.push({
        label: t("commissions.advance.attrs.gainers._label"),
        children: advance.gainers.map(({ gainer, rate }, index) => (
          <Fragment key={gainer.id}>
            {index > 0 && ", "}
            {hasReadAgentPermission ? (
              <Link to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: gainer.id })} target="_blank">
                {formatAgentName(gainer)}
              </Link>
            ) : (
              formatAgentName(gainer)
            )}
            {isDefined(rate) && ` - ${formatLocalePercentageNumber(rate)}`}
          </Fragment>
        )),
        span: 3,
        styles: { content: { whiteSpace: "normal" } },
      });
    }

    items.push(
      {
        label: t("commissions.advance.attrs.advanceAmount"),
        children: formatLocaleCurrency(advance.advanceAmount),
      },
      {
        label: t("commissions.advance.attrs.dueFeePaymentsCount"),
        children: advance.dueFeePaymentsCount,
      },
      {
        label: t("commissions.advance.attrs.paidFeePaymentsCount"),
        children: advance.paidFeePaymentsCount,
      },
      {
        label: t("commissions.advance.attrs.overdueFeePaymentsCount"),
        children: Math.max(advance.overdueFeePaymentsCount, 0),
        span: hasCommissionsPermission ? 1 : 3,
      },
    );

    if (hasCommissionsPermission) {
      items.push({
        label: t("commissions.advance.attrs.surplusAmount"),
        children: isDefined(advance.surplusAmount) ? formatLocaleCurrency(advance.surplusAmount) : undefined,
        span: 2,
      });
    }

    if (advance.status === CommissionAdvanceStatus.CANCELLED) {
      if (hasCommissionsPermission) {
        items.push({
          label: t("commissions.advance.attrs.cancellationCommission"),
          children: advance.cancellationCommission ? (
            <CommissionBatchName
              batchName={advance.cancellationCommission.batch.name}
              batchId={advance.cancellationCommission.batch.id}
              showLink
            />
          ) : undefined,
        });
      }

      items.push({
        label: t("commissions.advance.attrs.cancelledAt"),
        children: advance.cancelledAt ? formatLocaleDate(advance.cancelledAt) : undefined,
        span: hasCommissionsPermission ? 1 : 3,
      });

      if (hasCommissionsPermission) {
        items.push({
          label: t("commissions.advance.attrs.cancellationAmount"),
          children: isDefined(advance.cancellationAmount)
            ? formatLocaleCurrency(advance.cancellationAmount)
            : undefined,
        });
      }

      items.push({
        label: t("commissions.advance.enums.cancellationReason._label"),
        children: advance.cancellationReason
          ? t("commissions.advance.enums.cancellationReason." + advance.cancellationReason)
          : undefined,
        span: 3,
        styles: { content: { whiteSpace: "normal" } },
      });
    }

    if (advance.status === CommissionAdvanceStatus.COMPLETED) {
      items.push({
        label: t("commissions.advance.attrs.completedAt"),
        children: advance.completedAt ? formatLocaleDate(advance.completedAt) : undefined,
        span: 3,
      });
    }

    return items;
  }, [advance, hasCommissionsPermission, hasReadAgentPermission, t]);

  return (
    <>
      {advance.manualCancellationReason && advance.manualCancellationRequestedAt && (
        <Alert
          type="warning"
          showIcon
          className="margin-bottom-medium"
          title={t("commissions.advance.helpers.pendingCancellation", {
            reason: t("commissions.advance.enums.cancellationReason." + advance.manualCancellationReason),
            date: formatLocaleDate(advance.manualCancellationRequestedAt),
          })}
        />
      )}

      <Descriptions
        bordered
        size="small"
        column={3}
        styles={{ label: { width: 200 }, content: { whiteSpace: "nowrap" } }}
        items={items}
      />

      <Divider titlePlacement="start" style={{ marginTop: 48 }}>
        {t("commissions.advance.attrs.cycles._label")}
      </Divider>

      <CommissionAdvanceCyclesTable advance={advance} />

      {hasCommissionsPermission && (
        <>
          <Divider titlePlacement="start" style={{ marginTop: 48 }}>
            {t("commissions.advance.attrs.inputs._label")}
          </Divider>

          <CommissionAdvanceInputsTable advance={advance} />
        </>
      )}
    </>
  );
};
